import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MdbOptionModule, MdbSelectModule } from 'mdb5-adapted';
import { catchError, forkJoin, map, of } from 'rxjs';
import { AreaModel, BranchModel, RegionModel } from 'src/app/core/models';

import { BranchService } from '@lmsrestServices';

export type RegionAreaBranchStartLevel = 'region' | 'area' | 'branch';
export type RegionAreaBranchDisplayMode = 'cascading' | 'grouped-listbox';
export type RegionAreaBranchLayout = 'horizontal' | 'vertical';

export interface RegionAreaBranchSelection {
  region: RegionModel | null;
  area: AreaModel | null;
  branch: BranchModel | null;
}

@Component({
  selector: 'app-region-area-branch-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, MdbSelectModule, MdbOptionModule],
  templateUrl: './region-area-branch-selector.component.html',
  styleUrls: ['./region-area-branch-selector.component.scss'],
})
export class RegionAreaBranchSelectorComponent implements OnInit, OnChanges {
  @Input() startLevel: RegionAreaBranchStartLevel = 'region';
  @Input() required = false;
  @Input() parentRegion: RegionModel | null = null;
  @Input() parentArea: AreaModel | null = null;
  @Input() initialSelection: Partial<RegionAreaBranchSelection> | null = null;
  @Input() displayMode: RegionAreaBranchDisplayMode = 'cascading';
  @Input() layout: RegionAreaBranchLayout = 'horizontal';
  @Input() allowedRegions: RegionModel[] | null = null;
  @Input() allowedAreas: AreaModel[] | null = null;
  @Input() allowedBranches: BranchModel[] | null = null;


  @Output() selectionChange = new EventEmitter<RegionAreaBranchSelection>();

  regions: RegionModel[] = [];
  areas: AreaModel[] = [];
  branches: BranchModel[] = [];
  groupedBranchOptions: Array<{ label: string; area: AreaModel | null; region: RegionModel | null; branches: BranchModel[] }> = [];

  selectedRegion: RegionModel | null = null;
  selectedArea: AreaModel | null = null;
  selectedBranch: BranchModel | null = null;

  constructor(private branchService: BranchService) {}

  ngOnInit(): void {
    this.initializeByStartLevel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allowedRegions'] || changes['allowedAreas'] || changes['allowedBranches']) {
      this.applyAllowedCriteriaFilters();
    }

    if (changes['parentRegion'] && this.startLevel === 'area') {
      const areaStartRegion = this.getAreaStartRegionCandidate();
      this.selectedRegion = areaStartRegion;
      this.loadAreasByRegion(areaStartRegion, true);
    }

    if (changes['parentArea'] && this.startLevel === 'branch') {
      this.selectedArea = this.parentArea;
      this.loadBranchesByArea(this.parentArea, true);
    }

    if (changes['initialSelection'] && this.initialSelection) {
      if (this.startLevel === 'area') {
        const areaStartRegion = this.getAreaStartRegionCandidate();
        if (this.areas.length === 0) {
          this.selectedRegion = areaStartRegion;
          this.loadAreasByRegion(areaStartRegion, true);
          return;
        }
      }

      this.tryApplyInitialSelection();
    }
  }

  get showRegion(): boolean {
    if (this.isGroupedListboxMode) {
      return this.startLevel === 'region';
    }

    return this.startLevel === 'region';
  }

  get showArea(): boolean {
    if (this.isGroupedListboxMode) {
      return false;
    }

    return this.startLevel === 'region' || this.startLevel === 'area';
  }

  get showBranch(): boolean {
    if (this.isGroupedListboxMode) {
      return false;
    }

    return true;
  }

  get showGroupedBranchListbox(): boolean {
    return this.isGroupedListboxMode;
  }

  get layoutClass(): string {
    return this.layout === 'vertical' ? 'rab-vertical' : 'rab-horizontal';
  }

  get isGroupedListboxDisabled(): boolean {
    if (this.startLevel === 'region') {
      return !this.selectedRegion || this.groupedBranchOptions.length === 0;
    }

    if (this.startLevel === 'area') {
      return !this.selectedRegion || this.groupedBranchOptions.length === 0;
    }

    return !this.parentArea || this.groupedBranchOptions.length === 0;
  }

  private get isGroupedListboxMode(): boolean {
    return this.displayMode === 'grouped-listbox';
  }

  get isAreaDisabled(): boolean {
    return this.startLevel === 'region' && !this.selectedRegion;
  }

  get isBranchDisabled(): boolean {
    if (this.startLevel === 'region' || this.startLevel === 'area') {
      return !this.selectedArea;
    }

    return !this.parentArea;
  }

  onRegionChange(region: RegionModel | null): void {
    const isSameRegion =
      (!!this.selectedRegion && !!region &&
        (this.selectedRegion.id === region.id || this.selectedRegion.name === region.name)) ||
      (!this.selectedRegion && !region);

    if (isSameRegion) {
      this.selectedRegion = region;
      if (region && this.areas.length === 0) {
        this.loadAreasByRegion(region, true);
      }
      this.emitSelection();
      return;
    }

    this.selectedRegion = region;
    this.selectedArea = null;
    this.selectedBranch = null;
    this.areas = [];
    this.branches = [];
    this.groupedBranchOptions = [];

    this.loadAreasByRegion(region, false);
    this.emitSelection();
  }

  onAreaChange(area: AreaModel | null): void {
    const isSameArea =
      (!!this.selectedArea && !!area &&
        (this.selectedArea.id === area.id || this.selectedArea.name === area.name)) ||
      (!this.selectedArea && !area);

    if (isSameArea) {
      this.selectedArea = area;
      if (area && this.branches.length === 0) {
        this.loadBranchesByArea(area, true);
      }
      this.emitSelection();
      return;
    }

    this.selectedArea = area;
    this.selectedBranch = null;
    this.branches = [];
    this.groupedBranchOptions = [];

    this.loadBranchesByArea(area, false);
    this.emitSelection();
  }

  onBranchChange(branch: BranchModel | null): void {
    this.selectedBranch = branch;
    this.emitSelection();
  }

  onGroupedBranchChange(branch: BranchModel | null): void {
    this.selectedBranch = branch;

    if (!branch) {
      this.selectedArea = null;
      if (this.startLevel === 'region') {
        this.selectedRegion = null;
      }
      this.emitSelection();
      return;
    }

    const match = this.groupedBranchOptions.find((group) =>
      group.branches.some((b) => b.id === branch.id || b.branchSerial === branch.branchSerial)
    );

    if (match) {
      this.selectedArea = match.area;
      if (this.startLevel === 'region') {
        this.selectedRegion = match.region;
      }
    }

    this.emitSelection();
  }

  private initializeByStartLevel(): void {
    if (this.startLevel === 'region') {
      this.loadRegions();
      return;
    }

    if (this.startLevel === 'area') {
      const areaStartRegion = this.getAreaStartRegionCandidate();
      this.selectedRegion = areaStartRegion;
      this.loadAreasByRegion(areaStartRegion, true);
      return;
    }

    this.selectedArea = this.parentArea;
    this.loadBranchesByArea(this.parentArea, true);
  }

  private loadRegions(): void {
    this.branchService.getAllRegions().subscribe({
      next: (regions: RegionModel[]) => {
        this.regions = this.filterRegionsByAllowed(regions || []);
        this.tryApplyInitialSelection();
      },
      error: () => {
        this.regions = [];
      },
    });
  }

  private loadAreasByRegion(region: RegionModel | null, preserveSelection: boolean): void {
    if (!region) {
      this.areas = [];
      this.branches = [];
      this.groupedBranchOptions = [];
      if (!preserveSelection) {
        this.selectedArea = null;
        this.selectedBranch = null;
      }
    }

    this.branchService.getAllAreasByRegion(region).subscribe({
      next: (areas: AreaModel[]) => {
        this.areas = this.filterAreasByAllowed(areas || []);

        // MDB select compares object references. Rebind selected area to the
        // newly loaded options list so the selected value is rendered correctly.
        if (this.selectedArea && this.areas.length > 0) {
          this.selectedArea = this.findMatchingArea(this.selectedArea);
        }

        if (this.isGroupedListboxMode) {
          this.loadGroupedBranchesByAreas(this.areas, region, preserveSelection);
          return;
        }

        if (this.selectedArea && this.branches.length === 0) {
          this.loadBranchesByArea(this.selectedArea, preserveSelection);
          return;
        }

        if (preserveSelection) {
          this.tryApplyInitialSelection();
        }
      },
      error: () => {
        this.areas = [];
        this.groupedBranchOptions = [];
      },
    });
  }

  private loadBranchesByArea(area: AreaModel | null, preserveSelection: boolean): void {
    if (!area) {
      this.branches = [];
      this.groupedBranchOptions = [];
      if (!preserveSelection) {
        this.selectedBranch = null;
      }
      return;
    }

    this.branchService.getAllBranchesByArea(area).subscribe({
      next: (branches: BranchModel[]) => {
        this.branches = this.filterBranchesByAllowed(branches || []);

        // MDB select compares object references. Rebind selected branch to the
        // newly loaded options list so initial branch selection is retained.
        if (this.branches.length > 0) {
          const branchCandidate = this.selectedBranch || this.initialSelection?.branch || null;
          if (branchCandidate) {
            this.selectedBranch = this.findMatchingBranch(branchCandidate);
          }
        }

        if (this.isGroupedListboxMode) {
          this.groupedBranchOptions = [{
            label: area.name,
            area,
            region: area.region || this.parentRegion || this.selectedRegion || null,
            branches: this.branches,
          }];
        }

        if (preserveSelection) {
          this.tryApplyInitialSelection();
        }
      },
      error: () => {
        this.branches = [];
        this.groupedBranchOptions = [];
      },
    });
  }

  private loadGroupedBranchesByAreas(areas: AreaModel[], region: RegionModel | null, preserveSelection: boolean): void {
    const filteredAreas = this.filterAreasByAllowed(areas);

    if (!filteredAreas.length) {
      this.groupedBranchOptions = [];
      this.selectedBranch = null;
      this.emitSelection();
      return;
    }

    const branchCalls = filteredAreas.map((area) =>
      this.branchService.getAllBranchesByArea(area).pipe(
        map((branches) => ({
          label: area.name,
          area,
          region,
          branches: this.filterBranchesByAllowed(branches || []),
        })),
        catchError(() =>
          of({
            label: area.name,
            area,
            region,
            branches: [],
          })
        )
      )
    );

    forkJoin(branchCalls).subscribe((groups) => {
      this.groupedBranchOptions = groups.filter((group) => group.branches.length > 0);

      if (preserveSelection) {
        this.tryApplyInitialSelection();
      }
    });
  }

  private tryApplyInitialSelection(): void {
    if (!this.initialSelection) {
      return;
    }

    const initialRegion = this.getInitialRegionCandidate();

    if (this.startLevel === 'region' && initialRegion && this.regions.length > 0 && !this.selectedRegion) {
      this.selectedRegion = this.findMatchingRegion(initialRegion);
      this.loadAreasByRegion(this.selectedRegion, true);
      return;
    }

    if (this.showArea && !this.selectedArea && this.areas.length === 0) {
      return;
    }

    const initialArea = this.getInitialAreaCandidate() || this.getAreaCandidateFromBranch(this.initialSelection.branch || null);

    if (this.showArea && initialArea && this.areas.length > 0 && !this.selectedArea) {
      const matchedArea = this.findMatchingArea(initialArea);
      if (matchedArea) {
        this.selectedArea = matchedArea;
        this.loadBranchesByArea(this.selectedArea, true);
        return;
      }
    }

    if (this.initialSelection.branch && this.branches.length > 0 && !this.selectedBranch) {
      this.selectedBranch = this.findMatchingBranch(this.initialSelection.branch);
      if (!this.selectedArea) {
        const branchAreaCandidate = this.getAreaCandidateFromBranch(this.selectedBranch);
        if (branchAreaCandidate && this.areas.length > 0) {
          this.selectedArea = this.findMatchingArea(branchAreaCandidate);
        }
      }
      this.emitSelection();
      return;
    }

    if (this.initialSelection.branch && this.isGroupedListboxMode && this.groupedBranchOptions.length > 0 && !this.selectedBranch) {
      this.selectedBranch = this.findMatchingBranchFromGroups(this.initialSelection.branch);
      this.onGroupedBranchChange(this.selectedBranch);
    }
  }

  private getInitialRegionCandidate(): RegionModel | null {
    const region = this.initialSelection?.region;
    if (region?.id || region?.name) {
      return region;
    }

    const areaRegion = this.initialSelection?.area?.region;
    if (areaRegion?.id || areaRegion?.name) {
      return areaRegion;
    }

    const branchRegion = this.initialSelection?.branch?.area?.region;
    if (branchRegion?.id || branchRegion?.name) {
      return branchRegion;
    }

    return null;
  }

  private getInitialAreaCandidate(): AreaModel | null {
    const area = this.initialSelection?.area;
    if (area?.id || area?.name) {
      return area;
    }

    const branchArea = this.initialSelection?.branch?.area;
    if (branchArea?.id || branchArea?.name) {
      return branchArea;
    }

    return null;
  }

  private getAreaCandidateFromBranch(branch: BranchModel | null): AreaModel | null {
    if (!branch) {
      return null;
    }

    const branchArea = branch.area;
    if (branchArea?.id || branchArea?.name) {
      return branchArea;
    }

    const branchAreaId = Number((branch as any).area_id || 0);
    if (branchAreaId > 0) {
      return ({ id: branchAreaId, name: '' } as AreaModel);
    }

    return null;
  }

  private getAreaStartRegionCandidate(): RegionModel | null {
    return this.parentRegion || this.getInitialRegionCandidate();
  }

  private findMatchingRegion(region: RegionModel): RegionModel | null {
    return this.regions.find((r) => r.id === region.id || r.name === region.name) || null;
  }

  private findMatchingArea(area: AreaModel): AreaModel | null {
    return this.areas.find((a) => a.id === area.id || a.name === area.name) || null;
  }

  private findMatchingBranch(branch: BranchModel): BranchModel | null {
    return this.branches.find((b) =>
      b.id === branch.id ||
      b.branchSerial === branch.branchSerial ||
      b.branchName === branch.branchName
    ) || null;
  }

  private findMatchingBranchFromGroups(branch: BranchModel): BranchModel | null {
    const flattenedBranches = this.groupedBranchOptions.flatMap((group) => group.branches);
    return flattenedBranches.find((b) =>
      b.id === branch.id ||
      b.branchSerial === branch.branchSerial ||
      b.branchName === branch.branchName
    ) || null;
  }

  private applyAllowedCriteriaFilters(): void {
    this.regions = this.filterRegionsByAllowed(this.regions);
    this.areas = this.filterAreasByAllowed(this.areas);
    this.branches = this.filterBranchesByAllowed(this.branches);
    this.groupedBranchOptions = this.groupedBranchOptions
      .filter((group) => !group.area || this.isAreaAllowed(group.area))
      .map((group) => ({
        ...group,
        branches: this.filterBranchesByAllowed(group.branches),
      }))
      .filter((group) => group.branches.length > 0);

    if (this.selectedRegion && !this.isRegionAllowed(this.selectedRegion)) {
      this.selectedRegion = null;
      this.selectedArea = null;
      this.selectedBranch = null;
    }

    if (this.selectedArea && !this.isAreaAllowed(this.selectedArea)) {
      this.selectedArea = null;
      this.selectedBranch = null;
    }

    if (this.selectedBranch && !this.isBranchAllowed(this.selectedBranch)) {
      this.selectedBranch = null;
    }

    this.emitSelection();
  }

  private filterRegionsByAllowed(regions: RegionModel[]): RegionModel[] {
    if (!this.allowedRegions) {
      return regions;
    }
    return regions.filter((region) => this.isRegionAllowed(region));
  }

  private filterAreasByAllowed(areas: AreaModel[]): AreaModel[] {
    if (!this.allowedAreas) {
      return areas;
    }
    return areas.filter((area) => this.isAreaAllowed(area));
  }

  private filterBranchesByAllowed(branches: BranchModel[]): BranchModel[] {
    if (!this.allowedBranches) {
      return branches;
    }
    return branches.filter((branch) => this.isBranchAllowed(branch));
  }

  private isRegionAllowed(region: RegionModel): boolean {
    if (!this.allowedRegions) {
      return true;
    }
    return this.allowedRegions.some((allowed) =>
      (allowed.id != null && allowed.id === region.id) ||
      (!!allowed.name && allowed.name === region.name)
    );
  }

  private isAreaAllowed(area: AreaModel): boolean {
    if (!this.allowedAreas) {
      return true;
    }
    return this.allowedAreas.some((allowed) =>
      (allowed.id != null && allowed.id === area.id) ||
      (!!allowed.name && allowed.name === area.name)
    );
  }

  private isBranchAllowed(branch: BranchModel): boolean {
    if (!this.allowedBranches) {
      return true;
    }
    return this.allowedBranches.some((allowed) =>
      (allowed.id != null && allowed.id === branch.id) ||
      (allowed.branchSerial != null && allowed.branchSerial === branch.branchSerial) ||
      (!!allowed.branchName && allowed.branchName === branch.branchName)
    );
  }

  private emitSelection(): void {
    this.selectionChange.emit({
      region: this.selectedRegion,
      area: this.selectedArea,
      branch: this.selectedBranch,
    });
  }
}
